import { useState } from 'react'

import filter from 'lodash/filter'
import orderBy from 'lodash/orderBy'

import usePagination from 'modules/core/hooks/usePagination'
import { matchText } from 'modules/core/lib/stringUtils'

import {
  DEFAULT_SORT_ATTRIBUTE,
  DEFAULT_SORT_DIRECTION,
  PAGINATION_PER_PAGE,
} from 'modules/payroll_bundle/lib/constants'
import {
  ResultRowData,
  convertEmployeeResultsSummariesToResultsTableData,
} from 'modules/payroll_bundle/lib/resultsTableData'
import results, { Results } from 'modules/payroll_bundle/store/collections/results'

import { useEmployeeResultsSummaries } from './useEmployeeResultsSummaries'

const useSortResultConceptItemsTable = (tableData: ResultRowData[]) => {
  const [sort, setSort] = useState<{ attribute: string; direction: 'asc' | 'desc' }>({
    attribute: DEFAULT_SORT_ATTRIBUTE,
    direction: DEFAULT_SORT_DIRECTION,
  })

  function handleSort(attribute: string, direction: 'asc' | 'desc') {
    setSort({ attribute, direction })
  }

  return {
    tableData: orderBy(tableData, [sort.attribute], [sort.direction]),
    sort,
    onSort: handleSort,
  }
}

const useSearchResultConceptItemsTable = (tableData: ResultRowData[]) => {
  const [search, setSearch] = useState('')

  function handleSearch(searchTerm: string) {
    setSearch(searchTerm)
  }

  return {
    tableData: filter(tableData, (rowData) =>
      search ? matchText(search, rowData.employee?.access.fullName ?? '') : true
    ),
    search,
    onSearch: handleSearch,
  }
}

const usePaginationResultConceptItemsTable = (tableData: ResultRowData[]) => {
  const { paginatedItems, loadMore, hasMore, page } = usePagination({
    batchSize: PAGINATION_PER_PAGE,
    items: tableData,
  })

  return {
    tableData: paginatedItems,
    hasMore,
    page,
    onLoadMore: loadMore,
  }
}

export const useResultConceptItemsTable = (
  currentPolicyPeriodId?: string,
  previousPolicyPeriodId?: string,
  search?: string,
  sortField?: string,
  sortDirection?: string,
  sortConceptId?: string
) => {
  const currentPeriodResults = currentPolicyPeriodId
    ? results.filterByPolicyPeriod(currentPolicyPeriodId)
    : []
  const previousPeriodResults = previousPolicyPeriodId
    ? results.filterByPolicyPeriod(previousPolicyPeriodId)
    : []
  const {
    fetching,
    currentPeriodResultsSummaries,
    previousPeriodResultSummaries,
    mappedResultConcepts,
    fetchMore,
    hasNextPage,
    loadingMore,
    totalCount,
  } = useEmployeeResultsSummaries(
    currentPolicyPeriodId!,
    previousPolicyPeriodId,
    search,
    sortField,
    sortDirection,
    sortConceptId
  )

  const resultsTableData = convertEmployeeResultsSummariesToResultsTableData(
    [...currentPeriodResultsSummaries, ...previousPeriodResultSummaries],
    new Results([...currentPeriodResults, ...previousPeriodResults])
  )

  const { tableData: searchedTableData, ...searchControls } =
    useSearchResultConceptItemsTable(resultsTableData)
  const { tableData: sortedTabledData, ...sortControls } =
    useSortResultConceptItemsTable(searchedTableData)
  const { tableData: paginatedTableData, ...paginationControls } =
    usePaginationResultConceptItemsTable(sortedTabledData)

  return {
    fetching,
    resultsTableData: paginatedTableData,
    sortControls,
    searchControls,
    paginationControls: {
      totalCount: totalCount,
      hasMore: hasNextPage,
      loadingMore: loadingMore,
      onLoadMore: (nextPage: number) => {
        fetchMore({ variables: { offsetPage: nextPage } })
      },
    },
    mappedResultConcepts,
  }
}
